// ============================================================================
// CONFIG SETTINGS PANEL
// ============================================================================
// Right panel: scope selector, settings list, and save/discard footer.
// Ported from crs-web/src/components/modules/config-module/ConfigSettingsPanel.tsx.

import React, { useMemo, useCallback } from 'react';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect/SyutSelectUnified';
import { officeService } from 'api/services/officeService';
import { employeeService } from 'api/services/employeeService';
import ConfigSettingField from './ConfigSettingField';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types';
import type { ScopeType } from 'hooks/api/useConfigSettings';

interface ConfigSettingsPanelProps {
  activeGroup: ConfigGroup | null;
  scopeType: ScopeType;
  onScopeTypeChange: (scope: ScopeType) => void;
  selectedOfficeId: string | null;
  onOfficeSelect: (officeId: string | null) => void;
  selectedEmployeeId: string | null;
  onEmployeeSelect: (employeeId: string | null) => void;
  effectiveValues: ConfigEffectiveValue[];
  definitions: ConfigDefinition[];
  editedValues: Record<string, ConfigValueUpdate>;
  onValueChange: (configKey: string, value?: string, listValues?: ConfigListItem[]) => void;
  onResetValue: (configKey: string) => void;
  onSave: () => void;
  onDiscard: () => void;
  hasChanges: boolean;
  saving: boolean;
  loading: boolean;
}

const ConfigSettingsPanel: React.FC<ConfigSettingsPanelProps> = ({
  activeGroup,
  scopeType,
  onScopeTypeChange,
  selectedOfficeId,
  onOfficeSelect,
  selectedEmployeeId,
  onEmployeeSelect,
  effectiveValues,
  definitions,
  editedValues,
  onValueChange,
  onResetValue,
  onSave,
  onDiscard,
  hasChanges,
  saving,
  loading,
}) => {
  // Load office options for async SyutSelect
  const loadOfficeOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      const res = await officeService.getAll({
        searchTerm: inputValue || undefined,
        pageSize: inputValue ? 50 : 500,
        includeInactive: false,
      } as any);
      return (res.data ?? []).map((o: any) => ({
        value: o.id,
        label: `${o.code} - ${o.name}`,
      }));
    },
    []
  );

  // Load employee options
  const loadEmployeeOptions = useCallback(
    async (inputValue: string): Promise<SelectOption[]> => {
      const res = await employeeService.getAll({
        searchTerm: inputValue || undefined,
        pageSize: inputValue ? 50 : 500,
        includeInactive: false,
      } as any);
      return (res.data ?? []).map((e: any) => ({
        value: e.id,
        label: `${e.code} - ${e.name}`,
      }));
    },
    []
  );

  // Check if any definition supports EMPLOYEE scope
  const hasEmployeeScopeSettings = useMemo(() => {
    return definitions.some((d) => d.maxScopeLevel >= 2);
  }, [definitions]);

  // Sort definitions and match with effective values
  const sortedSettings = useMemo(() => {
    return definitions
      .slice()
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((def) => {
        const ev = effectiveValues.find((v) => v.configKey === def.configKey);
        return { definition: def, effectiveValue: ev };
      })
      .filter((item) => item.effectiveValue != null);
  }, [definitions, effectiveValues]);

  if (!activeGroup) {
    return (
      <div className="config-settings-panel">
        <div className="config-empty">Select a group to view settings</div>
      </div>
    );
  }

  return (
    <div className="config-settings-panel">
      {/* Header */}
      <div className="config-panel-header">
        <h2>{activeGroup.displayName}</h2>
        {activeGroup.description && <p>{activeGroup.description}</p>}
      </div>

      {/* Scope bar */}
      <div className="config-scope-bar">
        <div className="config-scope-toggle">
          <button
            type="button"
            className={`config-scope-toggle-btn${scopeType === 'SYSTEM' ? ' active' : ''}`}
            onClick={() => onScopeTypeChange('SYSTEM')}
          >
            System Defaults
          </button>
          <button
            type="button"
            className={`config-scope-toggle-btn${scopeType === 'OFFICE' ? ' active' : ''}`}
            onClick={() => onScopeTypeChange('OFFICE')}
          >
            Office Override
          </button>
          {hasEmployeeScopeSettings && (
            <button
              type="button"
              className={`config-scope-toggle-btn${scopeType === 'EMPLOYEE' ? ' active' : ''}`}
              onClick={() => onScopeTypeChange('EMPLOYEE')}
            >
              Employee Override
            </button>
          )}
        </div>
        {(scopeType === 'OFFICE' || scopeType === 'EMPLOYEE') && (
          <div className="config-scope-property-select">
            <SyutSelect
              isAsync
              loadOptions={loadOfficeOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={selectedOfficeId ?? undefined}
              onChange={(value: string | string[] | null) =>
                onOfficeSelect(typeof value === 'string' ? value : null)
              }
              placeholder="Select an office..."
              isClearable
              size="sm"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
            />
          </div>
        )}
        {scopeType === 'EMPLOYEE' && (
          <div className="config-scope-property-select">
            <SyutSelect
              isAsync
              loadOptions={loadEmployeeOptions}
              defaultOptions={true}
              cacheOptions={false}
              value={selectedEmployeeId ?? undefined}
              onChange={(value: string | string[] | null) =>
                onEmployeeSelect(typeof value === 'string' ? value : null)
              }
              placeholder="Select an employee..."
              isClearable
              size="sm"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPlacement="auto"
            />
          </div>
        )}
      </div>

      {/* Settings list */}
      <div className="config-settings-list">
        {loading ? (
          <div className="config-loading">Loading settings...</div>
        ) : scopeType === 'OFFICE' && !selectedOfficeId ? (
          <div className="config-empty">
            Select an office to view its configuration overrides
          </div>
        ) : scopeType === 'EMPLOYEE' && !selectedEmployeeId ? (
          <div className="config-empty">
            Select an employee to view its configuration overrides
          </div>
        ) : sortedSettings.length === 0 ? (
          <div className="config-empty">No settings found for this group</div>
        ) : (
          sortedSettings.map(({ definition, effectiveValue }) => (
            <ConfigSettingField
              key={definition.configKey}
              definition={definition}
              effectiveValue={effectiveValue!}
              editedValue={editedValues[definition.configKey]}
              onValueChange={onValueChange}
              onResetValue={onResetValue}
              scopeType={scopeType}
            />
          ))
        )}
      </div>

      {/* Footer bar */}
      {(hasChanges || saving) && (
        <div className="config-footer-bar">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDiscard}
            disabled={saving}
          >
            Discard Changes
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ConfigSettingsPanel;
